import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Clock,
  CheckCircle,
  Eye,
  Edit,
  ArrowLeft,
  Search,
  Download,
  X,
  Plus,
  Trash2,
  IndianRupee,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  sanitizeTextInput,
  sanitizeSearchInput,
  validateNumberInput,
  validatePhoneInput,
  handleTextInputChange,
  handleSearchInputChange,
  handleNumberInputChange,
  handlePhoneInputChange,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import tenderService from '../../../api/crm/tenders/tenderService';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { API_ENDPOINTS } from '../../../api/config';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const TenderLead = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialStatus = searchParams.get('status') || 'all';
  const initialPriority = searchParams.get('priority') || 'all';
  const initialAssignee = searchParams.get('assignee') || 'all';
  const initialStartDate = searchParams.get('startDate') || null;
  const initialEndDate = searchParams.get('endDate') || null;
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const initialSortBy = searchParams.get('sortBy') || 'submissionDate';
  const initialSortOrder = searchParams.get('sortOrder') || 'desc';
  const isInitialPageResetMount = useRef(true);

  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [searchInputRef] = useState(React.createRef());
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [priorityFilter, setPriorityFilter] = useState(initialPriority);
  const [selectedAssignee, setSelectedAssignee] = useState(initialAssignee);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: initialStartDate, endDate: initialEndDate });
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenderToDelete, setTenderToDelete] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editStatus, setEditStatus] = useState('Open');
  const [editPriority, setEditPriority] = useState('Medium');
  const [tenderForm, setTenderForm] = useState({
    title: '',
    organization: '',
    tenderValue: '',
    submissionDate: '',
    openingDate: '',
    location: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    requirements: [''],
    priority: 'Medium',
    assignedTo: ''
  });
  const [users, setUsers] = useState([]);
  const [allUsersMap, setAllUsersMap] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [summary, setSummary] = useState({
    totalTenders: 0,
    openTenders: 0,
    closedTenders: 0,
    cancelledTenders: 0,
    totalValue: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(initialSortBy);
  const [sortOrder, setSortOrder] = useState(initialSortOrder);

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'status', 'priority', 'assignee', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (priorityFilter && priorityFilter !== 'all') params.set('priority', priorityFilter);
      if (selectedAssignee && selectedAssignee !== 'all') params.set('assignee', selectedAssignee);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'submissionDate') params.set('sortBy', sortColumn);
      if (sortOrder && sortOrder !== 'desc') params.set('sortOrder', sortOrder);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    statusFilter,
    priorityFilter,
    selectedAssignee,
    dateRange,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    setSearchParams,
  ]);

  // Selection and export state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Sorting handlers
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortedTenders = () => {
    let result = (tenders || []);

    // Filter by createdAt date locally to ensure strictly visible only if within range
    if (dateRange.startDate || dateRange.endDate) {
      result = result.filter(tender => {
        const createDateStr = tender.createdAt || tender.createdAtDate;
        if (!createDateStr) return false;

        const tenderDate = new Date(createDateStr);
        tenderDate.setHours(0, 0, 0, 0);

        if (dateRange.startDate) {
          const startDate = new Date(dateRange.startDate);
          startDate.setHours(0, 0, 0, 0);
          if (tenderDate < startDate) return false;
        }

        if (dateRange.endDate) {
          const endDate = new Date(dateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          if (tenderDate > endDate) return false;
        }

        return true;
      });
    }

    if (!sortColumn || result.length === 0) return result;

    return [...result].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'title') {
        aValue = (a.title || '').toLowerCase();
        bValue = (b.title || '').toLowerCase();
      } else if (sortColumn === 'organization') {
        aValue = (a.organization || '').toLowerCase();
        bValue = (b.organization || '').toLowerCase();
      } else if (sortColumn === 'tenderValue') {
        aValue = Number(a.tenderValue) || 0;
        bValue = Number(b.tenderValue) || 0;
      } else if (sortColumn === 'submissionDate') {
        aValue = new Date(a.submissionDate || 0).getTime();
        bValue = new Date(b.submissionDate || 0).getTime();
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'priority') {
        aValue = (a.priority || '').toLowerCase();
        bValue = (b.priority || '').toLowerCase();
      } else if (sortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Fetch tenders from API (server-side filters + pagination)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const queryParams = {
          page: currentPage,
          limit: itemsPerPage,
        };

        if (debouncedSearchTerm) {
          queryParams.search = debouncedSearchTerm;
        }

        if (statusFilter !== 'all') {
          // Map lowercase filter to API enum
          const statusMap = {
            open: 'Open',
            closed: 'Closed',
            cancelled: 'Cancelled',
            completed: 'Completed',
            pending: 'Pending',
            process: 'Process',
          };
          queryParams.status = statusMap[statusFilter] || 'Open';
        }

        if (priorityFilter !== 'all') {
          const priorityMap = {
            high: 'High',
            medium: 'Medium',
            low: 'Low',
          };
          queryParams.priority = priorityMap[priorityFilter] || 'Medium';
        }

        if (selectedAssignee !== 'all') {
          queryParams.assignedTo = selectedAssignee;
        }

        if (dateRange.startDate) {
          queryParams.createdAtStart = dateRange.startDate;
        }
        if (dateRange.endDate) {
          queryParams.createdAtEnd = dateRange.endDate;
        }

        const result = await tenderService.getTenders(queryParams);

        if (result.success) {
          const apiTenders = (result.data || []).map((tender) => ({
            ...tender,
            ourBid: tender.ourBid || {
              submitted: false,
              bidAmount: null,
              submittedDate: null,
              status: 'Not Submitted',
            },
            requirements: Array.isArray(tender.requirements) ? tender.requirements : [],
            documents: Array.isArray(tender.documents) ? tender.documents : [],
          }));

          setTenders(apiTenders);

          if (result.pagination) {
            setPagination({
              currentPage: result.pagination.currentPage || currentPage,
              totalPages: result.pagination.totalPages || 1,
              totalItems: result.pagination.totalItems || apiTenders.length,
              limit: result.pagination.limit || itemsPerPage,
              hasNextPage: !!result.pagination.hasNextPage,
              hasPrevPage: !!result.pagination.hasPrevPage,
            });
          } else {
            // Fallback when no pagination meta is returned
            setPagination({
              currentPage,
              totalPages: 1,
              totalItems: apiTenders.length,
              limit: itemsPerPage,
              hasNextPage: false,
              hasPrevPage: false,
            });
          }
        } else {
          toast(result.error || 'Unable to load tenders. Please try again.', {
            icon: '⚠️',
            style: { background: '#f59e0b', color: '#fff' }
          });
          setTenders([]);
          setPagination((prev) => ({
            ...prev,
            totalItems: 0,
            totalPages: 1,
          }));
        }
      } catch (error) {
        toast('Unable to load tenders. Please check your connection and try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
        setTenders([]);
        setPagination((prev) => ({
          ...prev,
          totalItems: 0,
          totalPages: 1,
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearchTerm, statusFilter, priorityFilter, selectedAssignee, dateRange, currentPage, itemsPerPage]);

  // Helper: Fetch summary stats based on active filters
  const refreshSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const queryParams = {};
      if (debouncedSearchTerm.trim()) {
        queryParams.search = debouncedSearchTerm.trim();
      }
      if (statusFilter !== 'all') {
        const statusMap = {
          open: 'Open',
          closed: 'Closed',
          cancelled: 'Cancelled',
          completed: 'Completed',
          pending: 'Pending',
          process: 'Process',
        };
        queryParams.status = statusMap[statusFilter] || statusFilter;
      }
      if (priorityFilter !== 'all') {
        const priorityMap = {
          high: 'High',
          medium: 'Medium',
          low: 'Low',
        };
        queryParams.priority = priorityMap[priorityFilter] || priorityFilter;
      }
      if (selectedAssignee !== 'all') {
        queryParams.assignedTo = selectedAssignee;
      }
      if (dateRange.startDate) {
        queryParams.createdAtStart = dateRange.startDate;
      }
      if (dateRange.endDate) {
        queryParams.createdAtEnd = dateRange.endDate;
      }

      const result = await tenderService.getTenderSummary(queryParams);
      if (result.success && result.data) {
        setSummary(result.data);
      }
    } catch (error) {
    } finally {
      setSummaryLoading(false);
    }
  }, [debouncedSearchTerm, statusFilter, priorityFilter, selectedAssignee, dateRange]);

  // Refetch summary stats whenever active filters change
  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  // Fetch CRM team members for assignment (supports backend search)
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const params = new URLSearchParams();
        params.append('roleFilter', 'sales');

        if (assigneeSearchTerm && assigneeSearchTerm.trim().length >= 2) {
          params.append('search', assigneeSearchTerm.trim());
        }

        // If user is a sales manager, filter subordinates only
        if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
          params.append('managerId', user._id || user.id);
        }

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`;
        const response = await authFetch(url, {
          credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        if (result.responseCode === 200 && result.data) {
          const fetchedUsers = result.data.users || [];
          setUsers(fetchedUsers);
          setAllUsersMap(prev => {
            const next = { ...prev };
            fetchedUsers.forEach(u => {
              const id = u.user || u._id || u.id;
              if (id) next[id] = u;
            });
            return next;
          });
        }
      } catch (error) {
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [assigneeSearchTerm, user]);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'process', label: 'Process' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Process': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format users into SearchableDropdown options
  const assigneeOptions = useMemo(() => {
    const optionsMap = new Map();

    (users || []).forEach(u => {
      const id = u.user || u._id || u.id;
      if (!id) return;
      const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown';
      const email = u.email || '';
      const designation = u.designation || u.role || '';
      optionsMap.set(id, {
        id,
        displayText: `${name}${email ? ` (${email})` : ''}`,
        searchText: `${name} ${email} ${designation}`.toLowerCase(),
        title: name,
        email,
        designation,
      });
    });

    // Preserve currently selected assignee in options if present in allUsersMap
    if (selectedAssignee && selectedAssignee !== 'all' && selectedAssignee !== 'unassigned' && !optionsMap.has(selectedAssignee)) {
      const u = allUsersMap[selectedAssignee];
      if (u) {
        const id = u.user || u._id || u.id;
        const name = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown';
        const email = u.email || '';
        const designation = u.designation || u.role || '';
        optionsMap.set(id, {
          id,
          displayText: `${name}${email ? ` (${email})` : ''}`,
          searchText: `${name} ${email} ${designation}`.toLowerCase(),
          title: name,
          email,
          designation,
        });
      }
    }

    const transformed = Array.from(optionsMap.values()).sort((a, b) => (a.title || '').localeCompare(b.title || ''));

    return [
      { id: 'all', displayText: 'All Assignees', title: 'All Assignees', searchText: 'all assignees' },
      { id: 'unassigned', displayText: 'Unassigned', title: 'Unassigned', searchText: 'unassigned' },
      ...transformed,
    ];
  }, [users, selectedAssignee, allUsersMap]);

  // Reset to first page when filters/search change (skip on initial mount so
  // the URL-restored page is preserved on refresh)
  React.useEffect(() => {
    if (isInitialPageResetMount.current) {
      isInitialPageResetMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, priorityFilter, selectedAssignee, dateRange]);

  const hasActiveFilters =
    (searchTerm && searchTerm.trim() !== '') ||
    statusFilter !== 'all' ||
    priorityFilter !== 'all' ||
    selectedAssignee !== 'all' ||
    dateRange.startDate ||
    dateRange.endDate;

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setSelectedAssignee('all');
    setDateRange({ startDate: null, endDate: null });
    setCurrentPage(1);
  };

  const handleViewTender = (tenderId) => {
    navigate(`/tenders/${tenderId}`);
  };

  const handleEditTender = (tenderId) => {
    navigate(`/tenders/${tenderId}/edit`);
  };

  const handleDownloadDocument = (doc) => {
    // Simulate document download
    const link = document.createElement('a');
    link.href = `#`; // In real app, this would be the actual file URL
    link.download = doc.name;

    // Simulate download with a small delay
    setTimeout(() => {
      toast.success(`Downloading ${doc.name}...`);
    }, 100);
  };

  const handleGenerateTender = () => {
    navigate('/tenders/new');
  };

  const handleEditTenderSubmit = async () => {
    setIsEditing(true);
    try {
      const id = selectedTender._id || selectedTender.id;
      const updatePayload = {
        status: editStatus,
        priority: editPriority,
        assignedTo: (selectedTender.assignedTo && typeof selectedTender.assignedTo === 'object')
          ? selectedTender.assignedTo._id
          : selectedTender.assignedTo
      };

      const result = await tenderService.updateTender(id, updatePayload);

      if (result.success && result.data) {
        const updated = result.data;
        setTenders(prev =>
          prev.map(t =>
            String(t._id || t.id) === String(id)
              ? { ...t, ...updated }
              : t
          )
        );
        setSelectedTender(prev =>
          prev && String(prev._id || prev.id) === String(id)
            ? { ...prev, ...updated }
            : prev
        );
        toast.success('Tender updated successfully');
        setShowEditModal(false);
      } else {
        toast(result.error || 'Unable to update tender. Please check the information and try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {
      toast('Unable to update tender. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedTender(null);
  };

  const handleTenderFormChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;

    if (fieldType === 'text') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'number') {
      const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
      const minValue = NUMBER_LIMITS[field]?.min || 0;
      const numValidation = validateNumberInput(value, maxValue, minValue);
      processedValue = numValidation.value;
    } else if (fieldType === 'phone') {
      const phoneValidation = validatePhoneInput(value);
      processedValue = phoneValidation.value;
    } else if (fieldType === 'email') {
      processedValue = sanitizeSearchInput(String(value || '')); // Allows @ . - _
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'textarea') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    }

    setTenderForm(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const handleAddRequirement = () => {
    setTenderForm(prev => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  };

  const handleRemoveRequirement = (index) => {
    setTenderForm(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const handleRequirementChange = (index, value) => {
    setTenderForm(prev => ({
      ...prev,
      requirements: prev.requirements.map((req, i) => i === index ? value : req)
    }));
  };

  const handleGenerateTenderSubmit = async () => {
    // Validate required fields
    if (!tenderForm.title || !tenderForm.organization || !tenderForm.tenderValue ||
      !tenderForm.submissionDate || !tenderForm.openingDate) {
      toast('Please fill in all required fields before submitting.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
      return;
    }

    setIsGenerating(true);

    try {
      const payload = {
        ...tenderForm,
        requirements: tenderForm.requirements.filter(req => req && req.trim()),
      };

      const result = await tenderService.createTender(payload);

      if (result.success) {
        const newTender = {
          ...result.data,
          ourBid: result.data.ourBid || {
            submitted: false,
            bidAmount: null,
            submittedDate: null,
            status: 'Not Submitted',
          },
          requirements: Array.isArray(result.data.requirements) ? result.data.requirements : [],
          documents: Array.isArray(result.data.documents) ? result.data.documents : [],
        };
        setTenders(prev => [newTender, ...prev]);
        // Refresh global summary after creating a tender
        refreshSummary();
        toast.success('Tender created successfully');

        // Close modal and reset form
        setShowGenerateModal(false);
        setTenderForm({
          title: '',
          organization: '',
          tenderValue: '',
          submissionDate: '',
          openingDate: '',
          location: '',
          contactPerson: '',
          contactEmail: '',
          contactPhone: '',
          description: '',
          requirements: [''],
          priority: 'Medium',
          assignedTo: 'Sarah Johnson'
        });
      } else {
        toast(result.error || 'Unable to create tender. Please check all required fields and try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {
      toast('Unable to create tender. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteTenderClick = (tender) => {
    setTenderToDelete(tender);
    setShowDeleteModal(true);
  };

  const handleDeleteTender = async () => {
    if (!tenderToDelete) return;

    const tenderId = tenderToDelete._id || tenderToDelete.id;
    setDeletingId(tenderId);
    try {
      const result = await tenderService.deleteTender(tenderId);
      if (result.success) {
        setTenders(prev => prev.filter(t => String(t._id || t.id) !== String(tenderId)));
        if (selectedTender && String(selectedTender._id || selectedTender.id) === String(tenderId)) {
          setSelectedTender(null);
        }
        toast.success('Tender deleted successfully');
        // Refresh global summary after deletion
        refreshSummary();
      } else {
        toast(result.error || 'Unable to delete tender. Please try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {
      toast('Unable to delete tender. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setDeletingId(null);
      setShowDeleteModal(false);
      setTenderToDelete(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const selectedIds = Array.from(selectedRows);
      let successCount = 0;
      let lastError = null;

      for (const id of selectedIds) {
        const result = await tenderService.deleteTender(id);
        if (result.success) {
          successCount++;
        } else {
          lastError = result.error;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} tender(s)!`);
        setTenders(prev => prev.filter(t => !selectedRows.has(t._id || t.id)));
        setSelectedRows(new Set());
        refreshSummary();
      }

      if (lastError) {
        toast.error(lastError || 'Some items could not be deleted');
      }
      setShowBulkDeleteModal(false);
    } catch (error) {
      toast.error('An error occurred during bulk deletion');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const baseExportFilters = useMemo(() => {
    const statusMap = {
      open: 'Open',
      closed: 'Closed',
      cancelled: 'Cancelled',
    };
    const priorityMap = {
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    };
    return {
      search: debouncedSearchTerm.trim() || undefined,
      status:
        statusFilter !== 'all'
          ? statusMap[statusFilter] || statusFilter
          : undefined,
      priority:
        priorityFilter !== 'all'
          ? priorityMap[priorityFilter] || priorityFilter
          : undefined,
      assignedTo: selectedAssignee !== 'all' ? selectedAssignee : undefined,
      createdAtStart: dateRange.startDate || undefined,
      createdAtEnd: dateRange.endDate || undefined,
    };
  }, [
    debouncedSearchTerm,
    statusFilter,
    priorityFilter,
    selectedAssignee,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const currentPageIdsForExport = useMemo(
    () =>
      getSortedTenders()
        .map((item) => String(item._id || item.id))
        .filter(Boolean),
    [
      tenders,
      debouncedSearchTerm,
      statusFilter,
      priorityFilter,
      selectedAssignee,
      dateRange.startDate,
      dateRange.endDate,
      sortColumn,
      sortOrder,
    ]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageIdsForExport)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          module: 'crm-tender-leads',
          format,
          scope,
          filters: {
            ...baseExportFilters,
            ...(overrideFilters != null ? overrideFilters : {}),
          },
          page: overridePage ?? currentPage,
          limit: overrideLimit ?? itemsPerPage,
          sortBy: sortColumn,
          sortOrder,
          selectedIds,
          currentPageIds: Array.isArray(pageIds) ? pageIds : [],
        }),
      }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `tender-leads-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortOrder,
      currentPageIdsForExport,
    ]
  );

  const handleTenderLeadsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} row(s) exported.`);
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleTenderLeadsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const pageItems = getSortedTenders();
      const totalFiltered = pagination?.totalItems ?? 0;

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageItems.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No tenders match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
              createdAtStart: startDate,
              createdAtEnd: endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [baseExportFilters, pagination?.totalItems, tenders, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item._id || item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item._id || item.id));
      } else {
        items.forEach(item => newSelection.add(item._id || item.id));
      }
      return newSelection;
    });
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tender Leads</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Manage government and private tender opportunities
              </p>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <PermissionGate permission="lead_management" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={
                    selectedRows.size > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-600'
                      : ''
                  }
                >
                  {selectedRows.size > 0
                    ? `Export Selected (${selectedRows.size})`
                    : 'Export'}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleTenderLeadsSelectedFormatExport}
                />
              </div>
            </PermissionGate>

            {selectedRows.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                icon={isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {isBulkDeleting ? 'Deleting...' : `Delete (${selectedRows.size})`}
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              icon={<Download className="h-4 w-4" />}
              onClick={() => setShowBulkUploadModal(true)}
              className="hidden sm:flex"
            >
              Bulk Upload
            </Button>

            <PermissionGate permission="lead_management" action="create">
              <Button
                variant="primary"
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleGenerateTender}
                className="w-full sm:w-auto"
              >
                Generate Tender
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Stats Cards (global summary, not affected by filters/search) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Tenders</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 sm:mt-2">
                {summaryLoading ? '...' : formatIndianNumber(summary.totalTenders)}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-blue-500">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Open Tenders</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 sm:mt-2">
                {summaryLoading ? '...' : formatIndianNumber(summary.openTenders)}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-green-500">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Closed Tenders</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 sm:mt-2">
                {summaryLoading ? '...' : formatIndianNumber(summary.closedTenders)}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-gray-500">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
          </div>
        </div >

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 sm:mt-2">
                {summaryLoading
                  ? '...'
                  : `₹${summary.totalValue
                    ? formatIndianAmount(summary.totalValue)
                    : '0'}`}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-orange-500">
              <IndianRupee className="h-5 w-5 sm:h-6 sm:w-6 text-white font-bold text-xl" />
            </div>
          </div>
        </div>
      </div >

      {/* Search and Filters */}
      < div className="bg-white rounded-lg shadow-sm border border-gray-200" >
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tenders by title, ID or organization..."
                  value={searchTerm}
                  onChange={(e) => {
                    const sanitized = handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search);
                    setSearchTerm(sanitized);
                  }}
                  maxLength={FIELD_LIMITS.search}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0 flex-wrap">
              {!isSalesExecutive && (
                <div className="w-full sm:w-auto min-w-[200px] max-w-[280px]">
                  <SearchableDropdown
                    options={assigneeOptions}
                    value={selectedAssignee !== 'all' ? selectedAssignee : null}
                    onChange={(selectedId) => {
                      setSelectedAssignee(selectedId || 'all');
                    }}
                    onSearch={(term) => {
                      setAssigneeSearchTerm(term);
                    }}
                    placeholder={loadingUsers ? 'Loading...' : 'All Assignees'}
                    loading={loadingUsers}
                    searchPlaceholder="Search by name or email..."
                    minSearchLength={2}
                    debounceMs={300}
                    className="w-full"
                  />
                </div>
              )}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              >
                <option value="all">All Priority</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <div className="flex flex-col">
                {/* <span className="text-[10px] text-gray-400 ml-1 mb-0.5 uppercase font-medium">Filters by Created Date</span> */}
                <DateRangeFilter
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onChange={setDateRange}
                  allowFutureDates={true}
                />
              </div>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="flex-shrink-0 whitespace-nowrap"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tenders Table (similar style to Leads table) */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading tenders...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                        checked={getSortedTenders().length > 0 && getSortedTenders().every(item => selectedRows.has(item._id || item.id))}
                        onChange={() => handleSelectAll(getSortedTenders())}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tender</span>
                        {sortColumn === 'title' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('organization')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Organization</span>
                        {sortColumn === 'organization' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('tenderValue')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tender Value</span>
                        {sortColumn === 'tenderValue' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('submissionDate')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Submission Date</span>
                        {sortColumn === 'submissionDate' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === 'status' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('priority')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Priority</span>
                        {sortColumn === 'priority' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created</span>
                        {sortColumn === 'createdAt' ? (
                          sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedTenders().map((tender) => (
                    <tr key={tender._id || tender.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                          checked={selectedRows.has(tender._id || tender.id)}
                          onChange={() => handleSelectRow(tender._id || tender.id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="max-w-[16rem] text-sm font-medium text-gray-900 truncate capitalize">
                          {tender.title}
                        </div>
                        <div className="max-w-[16rem] text-xs text-gray-500 truncate">
                          {tender.tenderId || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="max-w-[16rem] text-sm text-gray-900 truncate capitalize">
                          {tender.organization}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹
                        {tender.tenderValue
                          ? formatIndianAmount(tender.tenderValue)
                          : '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(tender.submissionDate, { includeTime: false })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(
                            tender.status
                          )}`}
                        >
                          {tender.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getPriorityColor(
                            tender.priority
                          )}`}
                        >
                          {tender.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(tender.createdAt || tender.createdAtDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium text-left">
                        {tender.assignedTo && typeof tender.assignedTo === 'object' ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900">{`${tender.assignedTo.firstName || ''} ${tender.assignedTo.lastName || ''}`}</span>
                            <span className="text-[10px] text-gray-500 lowercase leading-tight">{tender.assignedTo.email}</span>
                          </div>
                        ) : (
                          <span className={tender.assignedTo ? "text-gray-900" : "text-gray-400 italic"}>
                            {tender.assignedTo || 'Unassigned'}
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewTender(tender._id || tender.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Tender"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditTender(tender._id || tender.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Edit Tender"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTenderClick(tender)}
                            className="text-red-600 hover:text-red-900"
                            disabled={deletingId === (tender._id || tender.id)}
                            title="Delete Tender"
                          >
                            {deletingId === (tender._id || tender.id) ? (
                              <span className="text-xs">Deleting...</span>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination using centralized component */}
        {
          !loading && pagination.totalItems > pagination.limit && (
            <PaginationControls
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={setCurrentPage}
              onLimitChange={(newLimit) => {
                setItemsPerPage(newLimit);
                setCurrentPage(1);
              }}
              limitOptions={[5, 10, 25, 50]}
            />
          )
        }

        {
          !loading && pagination.totalItems === 0 && (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tenders found</h3>
              <p className="text-gray-500">Try adjusting your search criteria or add a new tender.</p>
            </div>
          )
        }
      </div >

      {/* Tender Detail now uses dedicated page at /tenders/:id */}

      {/* Delete Tender Confirmation Modal */}
      {
        showDeleteModal && tenderToDelete && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Delete Tender</h3>
                <button
                  onClick={() => {
                    if (deletingId) return;
                    setShowDeleteModal(false);
                    setTenderToDelete(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete this tender?
                </p>
                <p className="text-xs text-gray-500">
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (deletingId) return;
                    setShowDeleteModal(false);
                    setTenderToDelete(null);
                  }}
                  disabled={!!deletingId}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleDeleteTender}
                  disabled={!!deletingId}
                  className="bg-red-600 hover:bg-red-700 border-red-600"
                >
                  {deletingId ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] !mt-0 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  Bulk Delete Tenders
                </h3>
                <button
                  onClick={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isBulkDeleting}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected tender(s)? This action cannot be undone.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isBulkDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  icon={isBulkDeleting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  className="bg-red-600 hover:bg-red-700"
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                >
                  {isBulkDeleting ? 'Deleting...' : `Delete All Selected (${selectedRows.size})`}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Generate Tender Modal */}
      {
        showGenerateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Generate New Tender</h3>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tender Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={tenderForm.title}
                        onChange={(e) => handleTenderFormChange('title', e.target.value, 'text', 200)}
                        placeholder="Enter tender title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        maxLength={200}
                        required
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(tenderForm.title, 200)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={tenderForm.organization}
                        onChange={(e) => handleTenderFormChange('organization', e.target.value, 'text', FIELD_LIMITS.company)}
                        placeholder="Enter organization name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        maxLength={FIELD_LIMITS.company}
                        required
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(tenderForm.organization, FIELD_LIMITS.company)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tender Value <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={tenderForm.tenderValue}
                        onChange={(e) => handleTenderFormChange('tenderValue', e.target.value, 'number', Infinity)}
                        placeholder="₹0.00"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <input
                        type="text"
                        value={tenderForm.location}
                        onChange={(e) => handleTenderFormChange('location', e.target.value, 'text', FIELD_LIMITS.city)}
                        placeholder="Enter location"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        maxLength={FIELD_LIMITS.city}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(tenderForm.location, FIELD_LIMITS.city)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={tenderForm.priority}
                        onChange={(e) => handleTenderFormChange('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Important Dates */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Important Dates</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Submission Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={tenderForm.submissionDate}
                        onChange={(e) => handleTenderFormChange('submissionDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Opening Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={tenderForm.openingDate}
                        onChange={(e) => handleTenderFormChange('openingDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        value={tenderForm.contactPerson}
                        onChange={(e) => handleTenderFormChange('contactPerson', e.target.value, 'text', FIELD_LIMITS.customerName)}
                        placeholder="Enter contact person name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        maxLength={FIELD_LIMITS.customerName}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(tenderForm.contactPerson, FIELD_LIMITS.customerName)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={tenderForm.contactEmail}
                        onChange={(e) => handleTenderFormChange('contactEmail', e.target.value, 'email', 100)}
                        placeholder="Enter contact email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        maxLength={100}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(tenderForm.contactEmail, 100)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={tenderForm.contactPhone}
                        onChange={(e) => handleTenderFormChange('contactPhone', e.target.value, 'phone')}
                        placeholder="Enter contact phone"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        maxLength={10}
                      />
                      <div className="flex justify-end mt-1">
                        <span className="text-xs text-gray-500">
                          {getCharacterCount(tenderForm.contactPhone, FIELD_LIMITS.phone)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To
                      </label>
                      <select
                        value={tenderForm.assignedTo}
                        onChange={(e) => handleTenderFormChange('assignedTo', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="">Select Assignee</option>
                        {users && Array.isArray(users) && users.map(user => (
                          <option key={user.user || user._id} value={user.user || user._id}>
                            {user.name || `${user.firstName || ''} ${user.lastName || ''} (${user.email || ''})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tender Description
                  </label>
                  <textarea
                    value={tenderForm.description}
                    onChange={(e) =>
                      handleTenderFormChange('description', e.target.value, 'textarea', FIELD_LIMITS.notes)
                    }
                    rows={4}
                    placeholder="Enter detailed description of the tender..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    maxLength={FIELD_LIMITS.notes}
                  />
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(tenderForm.description, FIELD_LIMITS.notes)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowGenerateModal(false)}
                  disabled={isGenerating}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerateTenderSubmit}
                  disabled={isGenerating}
                  icon={<FileText className="h-4 w-4" />}
                >
                  {isGenerating ? 'Generating...' : 'Generate Tender'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Tender Modal */}
      {
        showEditModal && selectedTender && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Edit Tender</h3>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tender Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={selectedTender.title}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organization <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={selectedTender.organization}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tender Value
                      </label>
                      <input
                        type="text"
                        value={selectedTender.tenderValue}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <input
                        type="text"
                        value={selectedTender.category}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                {/* Status and Priority */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Status & Priority</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="Open">Open</option>
                        <option value="Closed">Closed</option>
                        <option value="Cancelled">Cancelled</option>
                        <option value="Completed">Completed</option>
                        <option value="Pending">Pending</option>
                        <option value="Process">Process</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        value={selectedTender.contactPerson}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={selectedTender.contactEmail}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={selectedTender.contactPhone}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To
                      </label>
                      <select
                        value={typeof selectedTender.assignedTo === 'object' ? selectedTender.assignedTo?._id : selectedTender.assignedTo || ''}
                        onChange={(e) => setSelectedTender({ ...selectedTender, assignedTo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      >
                        <option value="">Select Assignee</option>
                        {users && Array.isArray(users) && users.map(user => (
                          <option key={user.user || user._id} value={user.user || user._id}>
                            {user.name || `${user.firstName || ''} ${user.lastName || ''} (${user.email || ''})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tender Description
                  </label>
                  <textarea
                    value={selectedTender.description}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  />
                </div>

                {/* Requirements */}
                <div>
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Requirements</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedTender.requirements.map((req, index) => (
                      <li key={index} className="text-sm text-gray-600">{req}</li>
                    ))}
                  </ul>
                </div>

                {/* Documents */}
                <div>
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Documents</h5>
                  <div className="space-y-2">
                    {selectedTender.documents.map((doc, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 border border-gray-200 rounded">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{doc.name}</span>
                        <span className="text-xs text-gray-500">({doc.size})</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="ml-auto"
                          onClick={() => handleDownloadDocument(doc)}
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={handleCloseEditModal}
                  disabled={isEditing}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleEditTenderSubmit}
                  disabled={isEditing}
                  icon={isEditing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Edit className="h-4 w-4" />}
                >
                  {isEditing ? 'Updating...' : 'Update Tender'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Tender Leads"
        showDateRange
        scopedFormatExport={{
          onExport: handleTenderLeadsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Upload Modal */}
      {
        showBulkUploadModal && (
          <BulkUploadModal
            isOpen={showBulkUploadModal}
            onClose={() => setShowBulkUploadModal(false)}
            onSuccess={() => {
              // refreshData();
              // Use existing state refreshers
              refreshSummary();
              // Trigger a re-fetch of the list by changing a dummy state if needed, 
              // but the useEffect with filters should re-run if we just call the fetch logic again.
              // Simplified: just reload the page or call the fetch function.
              // For now, let's assume the user can refresh or we trigger it.
              window.location.reload();
            }}
            config={{
              title: "Bulk Upload Tenders",
              subtitle: "Download the template and upload multiple tenders at once",
              templateEndpoint: "/crm/tenders/bulk-upload/template",
              validateEndpoint: "/crm/tenders/bulk-upload/validate",
              uploadEndpoint: "/crm/tenders/bulk-upload",
              templateFilename: "tender_bulk_template.csv",
              dataKey: "data",
              maxRecords: 100,
              instructions: [
                "Title, Organization, Tender Value, Submission Date, and Opening Date are required.",
                "Dates must be in YYYY-MM-DD format.",
                "Priority can be 'High', 'Medium', or 'Low' (defaults to Medium).",
                "Status can be 'Open', 'Closed', or 'Cancelled' (defaults to Open).",
                "Tender Value should be a number without currency symbols.",
                "Assigned To (Email) is optional. If provided, the tender will be assigned to the user with that email."
              ]
            }}
          />
        )
      }
    </div >
  );
};

export default TenderLead;
